'use client';

import { c as _c } from "react/compiler-runtime";
import { FactiiiGoalType, type Goal, type NewGoal, type SearchedGoal } from '@shared/all/domains/factiiis';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import toast from '@shared/utils/helpers/toast';
import { type InfiniteData, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { client } from '../../utils/trpc';
import Button from '../common/Button';
import Modal, { ModalContent, ModalFooter, ModalHeader } from '../HOC/Modal';
import Input from '../UI/Input';
import GoalItem from './GoalItem';
import GoalSearchSuggestions from './GoalSearchSuggestions';
type Props = {
  closeHandler: () => void;
};
const GoalModal = props => {
  const $ = _c(49);
  const [query, setQuery] = useState("");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [newGoals, setNewGoals] = useState(t0);
  let t1;
  if ($[1] !== newGoals) {
    t1 = newGoals.filter(_temp);
    $[1] = newGoals;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [buttonLoading, setButtonLoading] = useState(t1.length > 0);
  const queryClient = useQueryClient();
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["goals"];
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const queryKey = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["goals"];
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      queryKey: t3,
      queryFn: _temp2,
      initialPageParam: undefined as number | undefined,
      getNextPageParam: _temp3,
      select: _temp6
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const {
    data: data_0,
    hasNextPage,
    fetchNextPage
  } = useInfiniteQuery(t4);
  let t5;
  if ($[6] !== data_0 || $[7] !== newGoals) {
    t5 = () => {
      if (data_0) {
        const uniqueGoals = new Set(newGoals.map(_temp7));
        const filteredDataGoals = data_0.goals.filter(goal_2 => !uniqueGoals.has(`${goal_2.factiiiId}-${goal_2.type}`));
        setNewGoals([...newGoals, ...filteredDataGoals]);
      }
    };
    $[6] = data_0;
    $[7] = newGoals;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== data_0) {
    t6 = [data_0];
    $[9] = data_0;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  useEffect(t5, t6);
  let t7;
  if ($[11] !== buttonLoading || $[12] !== newGoals || $[13] !== queryClient) {
    t7 = async () => {
      const invalidGoals = newGoals.filter(_temp8);
      if (invalidGoals.length > 0) {
        toast("Some goals have invalid data. Please enter valid data in order to set goal.", "", "error");
        setButtonLoading(true);
        return;
      } else {
        setButtonLoading(false);
      }
      if (!buttonLoading && !(invalidGoals.length > 0)) {
        const [error] = await client.factiiis.updateGoal.mutate({
          goals: newGoals
        });
        if (!error) {
          queryClient.setQueryData(queryKey, oldData => {
            if (!oldData) {
              return;
            }
            return {
              pages: oldData.pages.map(page_0 => ({
                ...page_0,
                goals: newGoals
              })),
              pageParams: oldData.pageParams
            };
          });
        }
        setButtonLoading(false);
      }
    };
    $[11] = buttonLoading;
    $[12] = newGoals;
    $[13] = queryClient;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const submitHandler = t7;
  let t8;
  if ($[15] !== newGoals) {
    t8 = factiii => {
      const currentGoals = newGoals.filter(goal_4 => goal_4.factiiiId === factiii.id);
      const usedTypes = new Set(currentGoals.map(_temp9));
      const availableTypes = Object.values(FactiiiGoalType);
      const availableType = availableTypes.find(type => !usedTypes.has(type));
      if (availableType) {
        const newGoal = {
          id: factiii.id,
          name: factiii.name,
          avatarKey: factiii.avatarKey,
          factiiiId: factiii.id,
          factiiiRequirements: factiii.requirements,
          type: availableType,
          data: "",
          isNew: true,
          isValid: true,
          errorMessage: ""
        };
        setNewGoals(prevGoals => [newGoal, ...prevGoals]);
        setQuery("");
      } else {
        alert("All possible goal types for this item are already set.");
      }
      setQuery("");
    };
    $[15] = newGoals;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const goalClickHandler = t8;
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <h2>Goals</h2>;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== props.closeHandler) {
    t10 = <ModalHeader closeHandler={props.closeHandler}>{t9}</ModalHeader>;
    $[18] = props.closeHandler;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = value => setQuery(value);
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== query) {
    t12 = <Input value={query} onChange={t11} label="Search Goals" />;
    $[21] = query;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== goalClickHandler || $[24] !== query) {
    t13 = query.length > 0 && <GoalSearchSuggestions query={query} goalClickHandler={goalClickHandler} />;
    $[23] = goalClickHandler;
    $[24] = query;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== newGoals) {
    t14 = newGoals.length > 0 && <div className="space-y-6 text-start">{newGoals.map(goal_6 => <GoalItem key={goal_6.factiiiId + goal_6.type} goal={goal_6} setNewGoals={setNewGoals} newGoals={newGoals} setButtonLoading={setButtonLoading} />)}</div>;
    $[26] = newGoals;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t12 || $[29] !== t13 || $[30] !== t14) {
    t15 = <ModalContent><div className="space-y-6">{t12}{t13}{t14}</div></ModalContent>;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== buttonLoading || $[33] !== submitHandler) {
    t16 = <Button text="Update" onClick={submitHandler} disabled={buttonLoading} />;
    $[32] = buttonLoading;
    $[33] = submitHandler;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== data_0?.goals) {
    t17 = <Button text="Reset" onClick={() => {
      setNewGoals(data_0?.goals ?? []);
      setButtonLoading(false);
    }} borderTransparent={false} variant="SECONDARY" />;
    $[35] = data_0?.goals;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t16 || $[38] !== t17) {
    t18 = <ModalFooter>{t16}{t17}</ModalFooter>;
    $[37] = t16;
    $[38] = t17;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== fetchNextPage || $[41] !== hasNextPage) {
    t19 = hasNextPage && <div className="flex w-full justify-center"><InView onChange={isVisible => isVisible ? fetchNextPage() : undefined}><Loader2 className="animate-spin" height={50} width={50} /></InView></div>;
    $[40] = fetchNextPage;
    $[41] = hasNextPage;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== props.closeHandler || $[44] !== t10 || $[45] !== t15 || $[46] !== t18 || $[47] !== t19) {
    t20 = <Modal closeHandler={props.closeHandler}>{t10}{t15}{t18}{t19}</Modal>;
    $[43] = props.closeHandler;
    $[44] = t10;
    $[45] = t15;
    $[46] = t18;
    $[47] = t19;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  return t20;
};
export default GoalModal;
function _temp(goal) {
  return !goal.isValid;
}
function _temp2(t0) {
  const {
    pageParam
  } = t0;
  return unwrapFromResult(client.factiiis.goals.query({
    cursor: pageParam
  }));
}
function _temp3(lastPage) {
  const goals = lastPage.goals;
  if (goals.length < 10) {
    return;
  }
  return goals[goals.length - 1]?.id;
}
function _temp4(goal_0) {
  return {
    ...goal_0,
    isNew: false,
    isValid: true,
    errorMessage: ""
  };
}
function _temp5(page) {
  return page.goals.map(_temp4);
}
function _temp6(data) {
  return {
    goals: data.pages.flatMap(_temp5)
  };
}
function _temp7(goal_1) {
  return `${goal_1.factiiiId}-${goal_1.type}`;
}
function _temp8(goal_3) {
  return !goal_3.isValid || goal_3.data.length == 0;
}
function _temp9(goal_5) {
  return goal_5.type;
}